import { z } from 'zod';

/** Support tickets between a merchant and the platform team (ADR-0154, BR-258). */
export const SUPPORT_CATEGORIES = [
  'bug',
  'question',
  'billing',
  'device',
  'feature_request',
] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];

export const SUPPORT_STATUSES = ['open', 'waiting_merchant', 'resolved', 'closed'] as const;
export type SupportStatus = (typeof SUPPORT_STATUSES)[number];

export const SUPPORT_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type SupportPriority = (typeof SUPPORT_PRIORITIES)[number];

/** Screenshots are at most 5 MB before conversion to WebP. */
export const SUPPORT_SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;

const body = z.string().trim().min(1).max(5000);

// ------------------------------------------------------------------ merchant side
export const createSupportTicketSchema = z.object({
  subject: z.string().trim().min(1).max(200),
  category: z.enum(SUPPORT_CATEGORIES),
  description: body,
});
export type CreateSupportTicket = z.input<typeof createSupportTicketSchema>;

export const supportReplySchema = z.object({ body });
export type SupportReply = z.input<typeof supportReplySchema>;

export const supportMessageSchema = z.object({
  id: z.string(),
  authorType: z.enum(['merchant', 'operator']),
  /** The merchant member's name, or the operator's name on /platform; null for the merchant. */
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: z.string(),
});
export type SupportMessage = z.infer<typeof supportMessageSchema>;

const ticketHeader = {
  id: z.string(),
  reference: z.string(),
  subject: z.string(),
  category: z.enum(SUPPORT_CATEGORIES),
  status: z.enum(SUPPORT_STATUSES),
  lastMessageBy: z.enum(['merchant', 'operator']),
  lastMessageAt: z.string(),
  createdAt: z.string(),
};

export const merchantSupportTicketSchema = z.object({
  ...ticketHeader,
  openedByName: z.string(),
  hasScreenshot: z.boolean(),
  messages: z.array(supportMessageSchema),
});
export type MerchantSupportTicket = z.infer<typeof merchantSupportTicketSchema>;

export const merchantSupportTicketListSchema = z.object({
  tickets: z.array(
    z.object({ ...ticketHeader, openedByName: z.string(), messages: z.number().int() }),
  ),
});
export type MerchantSupportTicketList = z.infer<typeof merchantSupportTicketListSchema>;

// ------------------------------------------------------------------ platform side
const optionalUuid = z.uuid().optional();

export const platformSupportQuerySchema = z.object({
  status: z.enum(SUPPORT_STATUSES).optional(),
  priority: z.enum(SUPPORT_PRIORITIES).optional(),
  category: z.enum(SUPPORT_CATEGORIES).optional(),
  assignee: z.union([z.literal('none'), z.uuid()]).optional(),
  merchantId: optionalUuid,
  q: z
    .string()
    .trim()
    .max(100)
    .optional()
    .transform((v) => (v ? v : undefined)),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PlatformSupportQuery = z.input<typeof platformSupportQuerySchema>;

const platformHeader = {
  ...ticketHeader,
  /** The company's own reference (T-0004), the one the merchant sees as `reference`. */
  merchantReference: z.string(),
  merchantId: z.string(),
  merchantNameEn: z.string().nullable(),
  merchantNameAr: z.string().nullable(),
  priority: z.enum(SUPPORT_PRIORITIES),
  assigneeOperatorId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  openedByName: z.string(),
  firstResponseAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  /** Seconds from opening to the first operator reply; null before it. */
  firstResponseSeconds: z.number().int().nullable(),
  /** Seconds from opening to resolution; null while unresolved. */
  resolutionSeconds: z.number().int().nullable(),
};

export const supportCountsSchema = z.object({
  open: z.number().int(),
  waitingMerchant: z.number().int(),
  resolved: z.number().int(),
  closed: z.number().int(),
  unassignedOpen: z.number().int(),
  avgFirstResponseSeconds: z.number().int().nullable(),
  avgResolutionSeconds: z.number().int().nullable(),
});
export type SupportCounts = z.infer<typeof supportCountsSchema>;

export const platformSupportTicketListSchema = z.object({
  tickets: z.array(z.object({ ...platformHeader, messages: z.number().int() })),
  total: z.number().int(),
  counts: supportCountsSchema,
});
export type PlatformSupportTicketList = z.infer<typeof platformSupportTicketListSchema>;

export const supportNoteSchema = z.object({
  id: z.string(),
  operatorName: z.string(),
  body: z.string(),
  createdAt: z.string(),
});
export type SupportNote = z.infer<typeof supportNoteSchema>;

export const platformSupportTicketSchema = z.object({
  ...platformHeader,
  openedByEmail: z.string(),
  hasScreenshot: z.boolean(),
  version: z.number().int(),
  messages: z.array(supportMessageSchema),
  notes: z.array(supportNoteSchema),
});
export type PlatformSupportTicket = z.infer<typeof platformSupportTicketSchema>;

export const platformSupportReplySchema = z.object({
  body,
  /** The status after the reply; waiting for the merchant unless the operator says otherwise. */
  status: z.enum(SUPPORT_STATUSES).default('waiting_merchant'),
});
export type PlatformSupportReply = z.input<typeof platformSupportReplySchema>;

export const supportNoteInputSchema = z.object({ body });
export type SupportNoteInput = z.input<typeof supportNoteInputSchema>;

export const updateSupportTicketSchema = z.object({
  status: z.enum(SUPPORT_STATUSES),
  priority: z.enum(SUPPORT_PRIORITIES),
  assigneeOperatorId: z.uuid().nullable(),
  version: z.number().int().nonnegative(),
});
export type UpdateSupportTicket = z.input<typeof updateSupportTicketSchema>;

export const supportOperatorListSchema = z.object({
  operators: z.array(z.object({ id: z.string(), fullName: z.string() })),
});
export type SupportOperatorList = z.infer<typeof supportOperatorListSchema>;
